<template>
  <div class="advanced-search-page">
    <div class="card">
      <div
        v-if="dataValue.fields.length === 0"
        class="card-row card-row--empty-state"
      >
        <FiltersEmptyState>
          <template #title>
            {{ t('advancedsearch.empty.state.title') }}
          </template>
          <template #description>
            {{ t('advancedsearch.empty.state.description') }}
          </template>
        </FiltersEmptyState>
      </div>
      <div
        v-for="(field, idx) in dataValue.fields"
        :key="field.id"
        class="card-row"
      >
        <component
          :is="getFieldType(field.name)"
          v-model="field.value"
          class="f-field-advanced-search"
          :is-required="false"
          :validator-name="field.name"
          :field-name="getFieldLabelName(field.name)"
          :record-type="field.parserName"
          :component-idx="idx"
          @input="onChange"
          @change="onChange"
        />
        <button
          :aria-label="t('advancedsearch.remove.option')"
          class="f-field-search__erase--btn"
          @click="removeField(idx)"
        />
      </div>
      <div class="card-row advanced-search-page__btn-central btn-group">
        <button
          class="btn btn--light btn--outline"
          @click="openCriteria"
        >
          <span class="btn-text-icon">{{ t('advancedsearch.add.option') }}</span>
        </button>
        <button
          class="btn btn--light btn--outline"
          :disabled="!canOpenExpressions"
          @click="openExpression"
        >
          {{ t("advancedsearch.combine.option") }}
        </button>
        <p
          class="expr-status"
          :class="{'expr-status--warning': dataValue.status === 'invalid', 'expr-status--valid': dataValue.status === 'valid'}"
        >
          {{ t(`expression.${dataValue.status}`) }}
        </p>
      </div>
    </div>
    <CriteriaSearchModal
      :show-modal="criteriaModal"
      :parsers="parsers"
      :fields="dataValue.fields"
      @add-field="addField"
      @close-modal="criteriaModal = false"
    />
    <ExpressionEditorModal
      v-model="dataValue"
      :show-modal="expressionModal"
      @close-modal="closeExpressionModal"
      @change="onChange"
    />
  </div>
</template>

<script>
import Modal from '#src/components/utils/Modal.vue'
import StringField from '#src/components/advanced-search/fields/StringField.vue'
import KeywordField from '#src/components/advanced-search/fields/KeywordField.vue'
import SubjectField from '#src/components/advanced-search/fields/SubjectField.vue'
import TextAreaField from '#src/components/advanced-search/fields/TextAreaField.vue'
import EnumField from '#src/components/advanced-search/fields/EnumField.vue'
import NumberField from '#src/components/advanced-search/fields/NumberField.vue'
import DateField from '#src/components/advanced-search/fields/DateField.vue'
import UserField from '#src/components/advanced-search/fields/UserField.vue'
import BooleanField from '#src/components/advanced-search/fields/BooleanField.vue'
import ExpressionEditorModal from '#src/components/advanced-search/expression-editor/ExpressionEditorModal.vue'
import CriteriaSearchModal from '#src/components/advanced-search/CriteriaSearchModal.vue'
import FiltersEmptyState from '#src/components/empty-states/AdvancedSearch.vue'

import { MetadataUtils } from '@scholar/metadata-utils/main'
import RecordSearchFieldsHelpers from '#src/mixins/RecordSearchFieldsHelpers'
import SearchExpressionHelpers from '#src/mixins/SearchExpressionHelpers'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    Modal,
    StringField,
    KeywordField,
    TextAreaField,
    EnumField,
    NumberField,
    DateField,
    UserField,
    SubjectField,
    BooleanField,
    ExpressionEditorModal,
    CriteriaSearchModal,
    FiltersEmptyState
  },
  mixins: [RecordSearchFieldsHelpers, SearchExpressionHelpers],
  props: {
    // { fields: [], sequence: [], query: '', status: 'default' }
    modelValue: {
      type: Object,
      required: true
    },
    q: {
      type: String,
      default: ''
    },
    types: {
      type: Array,
      default: () => []
    },
    populateQuery: {
      type: Boolean,
      default: () => false
    }
  },
  emits: ['submit', 'update:value', 'open'],
  setup() {
    const { t } = useI18n({
      fallbackWarn: false,
      silentTranslationWarn: true,
      silentFallbackWarn: true,
      messages: {
        pt: {
          'expression.default': '* Os critérios estão a ser combinados da forma predefinida.',
          'expression.valid': '* Os critérios estão a ser combinados e a expressão é válida.',
          'expression.invalid': '* Os critérios estão a ser combinados mas a expressão é inválida.'
        },
        en: {
          'expression.default': '* The criteria are being combined in the default manner.',
          'expression.valid': '* The criteria are being combined and the expression is valid.',
          'expression.invalid': '* The criteria are being combined but the expression is not valid.'
        }
      }
    })
    return { t }
  },
  data() {
    return {
      parsers: {},
      criteriaModal: false,
      expressionModal: false
    }
  },
  computed: {
    filledFields: function () {
      return this.dataValue.fields.filter(f => !this.isFieldEmpty(f))
    },
    canOpenExpressions: function () {
      return this.filledFields.length > 0 && this.modelValue.fields.every(f => f.value.isValid)
    },
    canSearch: function () {
      return this.canOpenExpressions && this.modelValue.status !== 'invalid'
    },
    dataValue: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:value', val)
      }
    }
  },
  created() {
    this.parsers = Object.entries(new MetadataUtils().getAllParsers())
      .reduce((acc, [parserName, parserValue]) => {
        // filter out unsearchable fields
        acc[parserName] = Object.keys(parserValue.config)
          .filter(f =>
            ![
              'files',
              'peerreviewed', // FIXME: should be accessible
              'embargofct',
              'fundingReferences', // subfields are added below
              'authorEmail', // FIXME: should be accessible (?)
              'citationVolume',
              'citationStartPage',
              'citationEndPage',
              'citationEdition',
              'citationConferencePlace',
              'citationConferenceDate'
            ].includes(f)
          )
          .concat(
            'fundingReferences.funderName',
            'fundingReferences.funderIdentifier',
            'fundingReferences.fundingStream',
            'fundingReferences.awardTitle',
            'fundingReferences.awardNumber',
            'hasFiles')
        return acc
      }, {})
  },
  mounted() {
    // FIXME: does not work?
    if (this.populateQuery && (this.q || this.types.length > 0)) {
      this.modelValue.fields = ['title', 'type'].map(name => this.createField('other', name))
      this.modelValue.fields[0].value.value = this.q
      this.modelValue.fields[1].value.value = this.types
    }
    this.$emit('open')
  },
  methods: {
    onChange() {
      if (this.canSearch) {
        this.$emit('submit')
      }
    },
    closeExpressionModal() {
      this.expressionModal = false
      this.onChange()
    },
    createField(parserName, name) {
      return { id: Math.random(), parserName, name, value: { value: null, option: null, display: {}, isValid: true } }
    },
    openCriteria() {
      this.criteriaModal = true
    },
    addField({ parserName, fieldName }) {
      const newField = this.createField(parserName, fieldName)
      this.modelValue.fields.push(newField)
    },
    async removeField(index) {
      // this.modelValue.fields.splice(index, 1)
      this.modelValue.fields = this.modelValue.fields.filter((_, idx) => idx !== index)

      if (this.filledFields.length > 0) {
        this.modelValue.sequence = this.modelValue.sequence
          .filter(f => !(f.group === 'FieldNode' && f.value === index))
          .map((f) => {
            if (f.group === 'FieldNode' && f.value > index) {
              return { ...f, value: f.value - 1 }
            }
            return f
          })
      } else {
        this.modelValue.sequence = []
      }

      this.modelValue.query = this.getQueryFromSequence(this.modelValue.sequence)
      this.modelValue.status = this.expressionStatus(this.modelValue.query)
      this.onChange()
    },
    openExpression() {
      this.expressionModal = true
    }
  }
}
</script>

<style lang="scss" scoped>
@use "#src/assets/scss/variables" as *;

.card-row--empty-state {
  justify-content: center;
}

.expr-status {
  color: grey;
  font-size: 0.75rem;

  &--warning {
    color: red;
  }
  &--valid {
    color: green;
  }
}

.advanced-search-page {
  margin-bottom: 2rem;

  .advanced-search-page__description {
    max-width: 44.125rem;
    margin: 0 auto;
  }

  .advanced-search-page__btn-central {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .btn-text-icon {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;

    &::before {
      content: "";
      margin-right: 0.5rem;
      width: 1rem;
      height: 1rem;
      display: block;
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cg transform='rotate(45 8.839 7.682)' stroke='%232E3242' stroke-width='1.44' fill='none' fill-rule='evenodd'%3E%3Ccircle opacity='.6' cx='8.5' cy='8.5' r='7.072'/%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M5.97 5.97l5.009 5.009M11.027 5.97l-5.009 5.009'/%3E%3C/g%3E%3C/svg%3E");
      background-color: transparent;
      background-size: 1rem 1rem;
      background-position: 50% 50%;
      background-repeat: no-repeat;
    }
  }
}
</style>
